'use client'

import Link from 'next/link'
import { useLandingPageViewmodel } from '@/features/landing/landing-page.hooks'
import { LandingHomeLink } from '@/features/landing/view/LandingHomeLink'
import { LandingLeadForm } from '@/features/landing/view/LandingLeadForm'
import { routePaths } from '@/shared/config/site'
import { Button } from '@/shared/ui/Button'

export function RecruitersLandingPage() {
  const landingPageViewmodel = useLandingPageViewmodel()
  const { content } = landingPageViewmodel.page

  return (
    <div className="page-shell bg-[#050404] text-phosphor-ivory">
      <main className="relative overflow-hidden" id={'content'}>
        <div className="absolute left-5 top-5 z-20 md:left-8 md:top-8">
          <LandingHomeLink className="hover:bg-[#1c1510]" />
        </div>
        <section className="relative min-h-screen overflow-hidden px-5 pb-12 pt-20 md:px-8 md:pt-20 lg:px-10">
          <img
            alt={content.visual.alt}
            className="absolute inset-y-0 right-[-46rem] h-full max-w-none object-cover opacity-82 md:right-[-28rem] lg:right-[-16rem] xl:right-[-8rem]"
            decoding="async"
            height={852}
            src={content.visual.src}
            width={1800}
          />
          <div className="absolute inset-0 bg-[linear-gradient(90deg,#050404_0%,#050404_38%,rgba(5,4,4,0.38)_68%,#050404_100%)]" />
          <div className="relative mx-auto min-h-[calc(100svh-8rem)] max-w-7xl lg:pr-[29rem]">
            <div className="flex min-h-[calc(100svh-8rem)] flex-col justify-start">
              <h1 className="max-w-5xl font-sans text-4xl font-black leading-none text-[#f0e5cc] md:text-6xl lg:text-7xl">
                {content.title}
              </h1>
              <p className="mt-7 max-w-3xl text-xl font-semibold leading-9 text-white/78 md:text-2xl md:leading-10">
                {content.intro}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild className="border-[#f7b955] bg-[#f7b955] text-[#050404] hover:bg-[#ffcf6a]" size="lg">
                  <a href="#lead">{content.primaryCta}</a>
                </Button>
                <Button asChild className="border-white/12 bg-[#15100d] text-white hover:bg-[#21170f]" size="lg">
                  <Link href={routePaths.resume}>{content.secondaryCta}</Link>
                </Button>
              </div>
              <div className="mt-10 flex flex-wrap gap-3">
                {content.proofItems.map((item, index) => (
                  <article
                    key={item.title}
                    className={`max-w-xs bg-[#f0e5cc] p-5 text-[#120f0d] ${index === 1 ? 'lg:mt-8' : ''} ${
                      index === 2 ? 'lg:mt-3' : ''
                    }`}
                  >
                    <h2 className="text-base font-black leading-tight">{item.title}</h2>
                    <p className="mt-4 text-sm font-medium leading-7 text-[#120f0d]/72">{item.description}</p>
                  </article>
                ))}
              </div>
            </div>
            <LandingLeadForm className="mt-10 border-[#f7b955]/34 bg-[#15100d] lg:absolute lg:bottom-0 lg:right-0 lg:mt-0 lg:w-[27rem]" />
          </div>
        </section>
        <section className="mx-auto flex max-w-7xl flex-col gap-6 px-5 py-10 md:px-8 lg:flex-row lg:items-start lg:px-10">
          <div className="bg-[#ff3d5a] p-6 text-[#050404] md:p-8 lg:w-[24rem]">
            <h2 className="text-4xl font-black leading-none md:text-6xl">{content.processTitle}</h2>
          </div>
          <ol className="flex flex-1 flex-wrap items-start gap-4">
            {content.processItems.map((item, index) => (
              <li
                key={item}
                className={`min-w-64 flex-1 bg-[#15100d] p-5 ${index === 1 ? 'lg:mt-10' : ''} ${
                  index === 2 ? 'lg:mt-5' : ''
                }`}
              >
                <span className="font-mono text-sm font-semibold text-[#f7b955]">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <p className="mt-5 text-base font-semibold leading-7 text-white/72">{item}</p>
              </li>
            ))}
          </ol>
        </section>
        <section className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-8 md:px-8 lg:flex-row lg:items-start lg:px-10">
          <RecruiterList items={content.fitItems} title={content.fitTitle} />
          <RecruiterList warning items={content.boundaryItems} title={content.boundaryTitle} />
        </section>
        <section className="mx-auto grid max-w-7xl gap-5 px-5 pb-16 md:px-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:px-10">
          <div>
            <h2 className="max-w-4xl text-4xl font-black leading-none text-[#f0e5cc] md:text-6xl">
              {content.closingTitle}
            </h2>
            <p className="mt-5 max-w-3xl text-base font-semibold leading-8 text-white/66">{content.closingBody}</p>
          </div>
          <Button asChild className="border-[#f7b955] bg-[#f7b955] text-[#050404] hover:bg-[#ffcf6a]" size="lg">
            <a href="#lead">{content.primaryCta}</a>
          </Button>
        </section>
      </main>
    </div>
  )
}

function RecruiterList({ items, title, warning = false }: { items: string[]; title: string; warning?: boolean }) {
  return (
    <article className={warning ? 'flex-1 bg-[#1b080b] p-6' : 'flex-1 bg-[#f0e5cc] p-6 text-[#120f0d]'}>
      <h2 className={warning ? 'text-2xl font-black text-[#ff3d5a]' : 'text-2xl font-black'}>{title}</h2>
      <ul className="mt-5 space-y-3">
        {items.map((item) => (
          <li
            key={item}
            className={warning ? 'text-sm leading-7 text-white/66' : 'text-sm font-medium leading-7 text-[#120f0d]/76'}
          >
            {item}
          </li>
        ))}
      </ul>
    </article>
  )
}
